import { Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import * as m from '~/paraglide/messages'
import { Input } from '~/shared/ui/input'
import { SegmentedControl } from '~/shared/ui/segmented-control'

export type EventsView = 'list' | 'calendar'

const SEARCH_DEBOUNCE_MS = 300

interface EventsToolbarProps {
  search: string
  onSearchChange: (search: string) => void
  view: EventsView
  onViewChange: (view: EventsView) => void
}

export function EventsToolbar({ search, onSearchChange, view, onViewChange }: EventsToolbarProps) {
  const [draft, setDraft] = useState(search)
  const lastSent = useRef(search)

  useEffect(() => {
    if (search !== lastSent.current) {
      lastSent.current = search
      setDraft(search)
    }
  }, [search])

  useEffect(() => {
    if (draft === lastSent.current) return
    const timer = setTimeout(() => {
      lastSent.current = draft
      onSearchChange(draft)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draft, onSearchChange])

  const clear = () => {
    setDraft('')
    lastSent.current = ''
    onSearchChange('')
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="relative w-full sm:max-w-sm">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && draft) clear()
          }}
          placeholder={m.eventsSearchPlaceholder()}
          aria-label={m.eventsSearchLabel()}
          className="rounded-full pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden"
        />
        {draft && (
          <button
            type="button"
            onClick={clear}
            aria-label={m.eventsSearchClear()}
            className="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <div className="sm:ml-auto">
        <SegmentedControl
          label={m.eventsViewLabel()}
          value={view}
          options={[
            { value: 'list', label: m.eventsViewList() },
            { value: 'calendar', label: m.eventsViewCalendar() },
          ]}
          onChange={onViewChange}
        />
      </div>
    </div>
  )
}
